/**
 * Get Monday of the week containing the given date.
 */
export function getWeekStart(date) {
  const d = new Date(date)
  const day = d.getDay()
  // getDay() returns 0 for Sunday, 1 for Monday, etc.
  const diff = day === 0 ? -6 : 1 - day
  d.setDate(d.getDate() + diff)
  d.setHours(0, 0, 0, 0)
  return d
}

/**
 * Get array of Date objects for each day of the week starting from Monday.
 * Returns 5 days (Mon-Fri) by default, or 7 if includeWeekend is true.
 */
export function getWeekDays(weekStart, includeWeekend) {
  const count = includeWeekend ? 7 : 5
  const days = []
  for (let i = 0; i < count; i++) {
    const d = new Date(weekStart)
    d.setDate(d.getDate() + i)
    days.push(d)
  }
  return days
}

/**
 * Check if any events fall on Saturday or Sunday.
 */
export function hasWeekendEvents(events) {
  return events.some(e => {
    const day = new Date(e.from).getDay()
    return day === 0 || day === 6
  })
}

/**
 * Compute the hour range needed to display all events.
 * Default: 8:00-21:00, auto-expands if events fall outside.
 */
export function computeHourRange(events) {
  let min = 8
  let max = 21
  for (const e of events) {
    const fromHour = new Date(e.from).getHours()
    const toDate = new Date(e.to)
    const toHour = toDate.getHours() + (toDate.getMinutes() > 0 ? 1 : 0)
    if (fromHour < min) min = fromHour
    if (toHour > max) max = toHour
  }
  return { hourStart: min, hourEnd: max }
}

/**
 * Filter events that occur on the given day.
 */
export function getEventsForDay(events, day) {
  const dayStart = new Date(day)
  dayStart.setHours(0, 0, 0, 0)
  const dayEnd = new Date(day)
  dayEnd.setHours(23, 59, 59, 999)

  return events.filter(e => {
    const from = new Date(e.from)
    return from >= dayStart && from <= dayEnd
  })
}

/**
 * Compute overlap lanes for events within a single day.
 * Returns a Map from event id to { lane, totalLanes }.
 */
export function computeOverlapLanes(events) {
  if (events.length === 0) return new Map()

  // Sort by start time
  const sorted = [...events].sort((a, b) => new Date(a.from) - new Date(b.from))

  // Find overlap groups
  const groups = []
  let currentGroup = [sorted[0]]

  for (let i = 1; i < sorted.length; i++) {
    const evt = sorted[i]
    const evtFrom = new Date(evt.from)
    // Check if this event overlaps with any event in the current group
    const overlaps = currentGroup.some(g => evtFrom < new Date(g.to))
    if (overlaps) {
      currentGroup.push(evt)
    } else {
      groups.push(currentGroup)
      currentGroup = [evt]
    }
  }
  groups.push(currentGroup)

  // Assign lanes within each group
  const laneMap = new Map()
  for (const group of groups) {
    const totalLanes = group.length
    group.forEach((evt, lane) => {
      laneMap.set(evt.id, { lane, totalLanes })
    })
  }

  return laneMap
}

/**
 * Format a Date to "HH:MM" string.
 */
export function formatTime(date) {
  const d = new Date(date)
  const h = d.getHours().toString().padStart(2, '0')
  const m = d.getMinutes().toString().padStart(2, '0')
  return `${h}:${m}`
}

/**
 * Format a date range label for the week, e.g. "Feb 23 – Mar 1, 2026"
 */
export function formatWeekLabel(weekStart, weekEnd) {
  const opts = { month: 'short', day: 'numeric' }
  const startStr = weekStart.toLocaleDateString('en-US', opts)
  const sameMonth = weekStart.getMonth() === weekEnd.getMonth()
  const endStr = sameMonth
    ? weekEnd.getDate()
    : weekEnd.toLocaleDateString('en-US', opts)
  return `${startStr} – ${endStr}, ${weekEnd.getFullYear()}`
}

/**
 * Check if two dates are the same calendar day.
 */
export function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate()
}

/**
 * Get distinct months spanned by a week (for cross-month fetching).
 * Returns array of { year, month } objects.
 */
export function getMonthsInWeek(weekStart) {
  const weekEnd = new Date(weekStart)
  weekEnd.setDate(weekEnd.getDate() + 6)

  const months = [{ year: weekStart.getFullYear(), month: weekStart.getMonth() + 1 }]

  if (weekStart.getMonth() !== weekEnd.getMonth() || weekStart.getFullYear() !== weekEnd.getFullYear()) {
    months.push({ year: weekEnd.getFullYear(), month: weekEnd.getMonth() + 1 })
  }

  return months
}
